import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {Container, Row, Col, Form, Button, Alert, Spinner, Modal, OverlayTrigger, Tooltip,} from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import "./TagStyle.css";
import axios from "../AxiosConfig";

const CreateEditOrg = () => {
  const [formData, setFormData] = useState({orgName: "",
                                            branch: "",
                                            website: "",
                                            database: "",
                                            contactEmail: "",
                                            contactPhone: "",
                                            contactPerson: "",
                                            contactPersonPhone: "",
                                            summary: "",
                                            forProfit: false,});

  const [tags, setTags] = useState({myOrg: [],
                                    collaborative: [],
                                    regions: [],
                                    country: [],});

  const [selectedTags, setSelectedTags] = useState({myOrg: [],
                                                    collaborative: [],
                                                    regions: [],
                                                    country: [],});
  const renderTooltip = (message) => (
    <Tooltip>
      {message}
    </Tooltip>
  )
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newTag, setNewTag] = useState({ category: "", name: "" });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const regionsRes = await axios.get("/api/organizations/regions");
        const myOrgRes = await axios.get("/api/organizations/my_org_tags");
        const collaborativeRes = await axios.get("/api/organizations/collaborators");
        const countryRes = await axios.get("/api/organizations/countries");

        setTags({myOrg: myOrgRes.data,
                 collaborative: collaborativeRes.data,
                 regions: regionsRes.data.regions,
                 country: countryRes.data.countries,});
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    const fetchOrganization = async () => {
      try {
        const response = await axios.get("/api/organizations/organization");
        if (response.data) {
          setFormData({orgName: response.data.org_name,
                       branch: response.data.branch,
                       website: response.data.org_url,
                       database: response.data.db_url,
                       contactEmail: response.data.contact_email,
                       contactPhone: response.data.contact_phone,
                       contactPerson: response.data.contact_person,
                       contactPersonRole: response.data.contact_person_role,
                       summary: response.data.summary,
                       forProfit: response.data.for_profit,});

          setSelectedTags({myOrg: response.data.my_org_tags || [],
                           collaborative: response.data.collaborative || [],
                           regions: response.data.regions || [],
                           country: response.data.countries || [],});

          setIsEditMode(true);
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setIsEditMode(false);
        } else {
          setError("Error loading organization data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTags();

    if (location.state?.fromMyOrg) {
      fetchOrganization();
    } else {
      setLoading(false);
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleTagSelect = (e, tagType) => {
    const tagId = e.target.value;
    const tagName = e.target.options[e.target.selectedIndex].text;

    setSelectedTags((prev) => {
      if (!prev[tagType].some((tag) => tag.id === tagId)) {
        return {
          ...prev,
          [tagType]: [...prev[tagType], { id: tagId, name: tagName }],
        };
      }
      return prev;
    });
  };

  const handleTagRemove = (tagId, tagType) => {
    setSelectedTags((prev) => ({
      ...prev,
      [tagType]: prev[tagType].filter((tag) => tag.id !== tagId),
    }));
  };

  const handleCreateTag = () => {
    if (!newTag.category || !newTag.name.trim()) {
      console.error("Category or name is missing");
      return;
    }
  
    const newTagObject = {
      id: `temp-${Date.now()}`, // Temporary ID for new tags
      name: newTag.name.trim(),
      isNew: true,
    };
  
    setTags((prev) => ({
      ...prev,
      [newTag.category]: [...prev[newTag.category], newTagObject],
    }));
  
    setSelectedTags((prev) => ({
      ...prev,
      [newTag.category]: [...prev[newTag.category], newTagObject],
    }));
  
    setNewTag({ category: "", name: "" });
    setShowModal(false);
  };  

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const allTags = [
        ...selectedTags.myOrg,
        ...selectedTags.collaborative,
        ...selectedTags.regions,
        ...selectedTags.country,
      ];
  
      // Separate new tags from existing ones
      const newTags = allTags.filter((tag) => String(tag.id).startsWith("temp-"));
      const existingTags = allTags.filter((tag) => !String(tag.id).startsWith("temp-"));
  
      const data = {
        ...formData,
        newTags: newTags.map((tag) => ({
          name: tag.name,
          category: Object.entries(tags).find(([key, value]) =>
            value.some((t) => t.id === tag.id)
          )[0], 
        })),
        existingTagIds: existingTags.map((tag) => Number(tag.id)), // Ensure IDs are numeric
      };
  
      if (isEditMode) {
        await axios.put("/api/organizations/update_organization", data);
      } else {
        await axios.post("/api/organizations/create_organization", data);
      }
  
      navigate("/MyOrg");
    } catch (error) {
      console.error("Error submitting organization data:", error);
      setError("Failed to submit organization data.");
    }
  };
  

  if (loading) return <Spinner animation="border" role="status" className="mt-5" />;
  if (error) return <Alert variant="danger" className="mt-5">{error}</Alert>;

  return (
    <Container className="mt-5">
      <Row>
        <Col md={6}>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="orgName" className="mb-3">
              <Form.Label>Organization Name</Form.Label>
              <Form.Control type="text" name="orgName" required value={formData.orgName} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="branch" className="mb-3">
              <Form.Label>Department, Branch, or Division</Form.Label>
              <Form.Control type="text" name="branch" value={formData.branch} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="website" className="mb-3">
              <Form.Label>Organization Website</Form.Label>
              <Form.Control type="url" name="website" required value={formData.website} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="database" className="mb-3">
              <Form.Label>Organization Database Website</Form.Label>
              <Form.Control type="text" name="database" value={formData.database} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="contactPerson" className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="contactPersonRole" className="mb-3">
              <Form.Label>Contact Person Role In Organization</Form.Label>
              <Form.Control type="text" name="contactPersonRole" value={formData.contactPersonRole} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="contactEmail" className="mb-3">
              <Form.Label>Contact Email</Form.Label>
              <Form.Control type="email" name="contactEmail" required value={formData.contactEmail} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="contactPhone" className="mb-3">
              <Form.Label>Contact Phone</Form.Label>
              <Form.Control type="tel" name="contactPhone" required value={formData.contactPhone} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="summary" className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control as="textarea" name="summary" required value={formData.summary} onChange={handleChange} />
            </Form.Group>
            <Form.Group controlId="country" className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control as="select" onChange={(e) => handleTagSelect(e, "country")}>
                <option value="">Select Country</option>
                {tags.country.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Control>
              <ul className="d-flex flex-wrap list-unstyled mt-2">
                {selectedTags.country.map((tag) => (
                  <li key={tag.id} className="tag-style">
                    <span className="me-2">{tag.name}</span>
                    <button
                      type="button"
                      className="btn-close btn-sm btn-close-white"
                      aria-label={`Remove ${tag.name}`}
                      onClick={() => handleTagRemove(tag.id, "country")}
                      style={{ fontSize: "0.8rem" }}
                    ></button>
                  </li>
                ))}
              </ul>
            </Form.Group>
            <Form.Group controlId="forProfit" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Check if it is a for-profit organization"
                name="forProfit"
                checked={formData.forProfit}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Col>
        <Col md={6}>
          {["myOrg", "collaborative", "regions"].map((tagType) => (
            <Form.Group controlId={tagType} className="mb-3" key={tagType}>
              <Form.Label className="d-flex align-items-center">
                {tagType === "myOrg" ? "Data My Organization Collects" : 
                 tagType === "collaborative" ? "Collaborative Interests" : 
                 "Regions"}
                 <OverlayTrigger placement="bottom"
                                 overlay={renderTooltip(tagType === "myOrg" ? "This is the data your organization collects and manages." :
                                          tagType === "collaborative" ? "These are the areas where your organization is open to collaboration." : 
                                          "Select the regions relevant to your organization.")}>

                <InfoCircle size={18} className="ms-2 text-muted" style={{ cursor: "pointer" }} />
                 </OverlayTrigger>
              </Form.Label>
              <Form.Control as="select" onChange={(e) => handleTagSelect(e, tagType)}>
                <option value="">Select {tagType.charAt(0).toUpperCase() + tagType.slice(1)}</option>
                {tags[tagType].map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Control>
              <ul className="d-flex flex-wrap list-unstyled mt-2">
                {selectedTags[tagType].map((tag) => (
                  <li key={tag.id} className="badge bg-secondary text-light d-flex align-items-center me-2 mb-2">
                    <span className="me-2">{tag.name}</span>
                    <button
                      type="button"
                      className="btn-close btn-sm btn-close-white"
                      aria-label={`Remove ${tag.name}`}
                      onClick={() => handleTagRemove(tag.id, tagType)}
                      style={{ fontSize: "0.8rem" }}
                    ></button>
                  </li>
                ))}
              </ul>
            </Form.Group>
          ))}
          <div className="d-flex align-items-center">
            <p className="mb-0 me-2">Don't see a tag you need?</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              Create New Tag
            </button>
          </div>
        </Col>
      </Row>
      <Button className="d-block mx-auto mt-4 mb-4" variant="primary" onClick={handleSubmit}>
        {isEditMode ? "Update Organization" : "Create Organization"}
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>New Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="tagCategory">
            <Form.Label>Category</Form.Label>
            <Form.Control as="select" value={newTag.category} onChange={(e) => setNewTag((prev) => ({ ...prev, category: e.target.value }))}>
              <option value="">Select Category</option>
              <option value="myOrg">My Organization Collects</option>
              <option value="collaborative">Collaborative Interests</option>
              <option value="regions">Regions</option>
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="tagName">
            <Form.Label>Tag Name</Form.Label>
            <Form.Control type="text" value={newTag.name} onChange={(e) => setNewTag((prev) => ({ ...prev, name: e.target.value }))}/>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCreateTag}>
            Create Tag
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CreateEditOrg;
